import { atom } from "nanostores";
import { ApiError, type CompassDoc, getCompass, putCompass } from "../net/api";
import { describeError } from "../ui/errors";
import { toast } from "./stores";

export const MAX_MARKS = 20;
export const MAX_NAME_CHARS = 24;

export const $compass = atom<CompassDoc>({ hide: [], marks: [] });
/** False until a read succeeds: every save replaces the whole document, so saving an unread one would wipe the stored marks (spec 2e §5). */
export const $compassLoaded = atom(false);
/** True while a save is in flight: edits wait, so a double click cannot act twice and responses cannot arrive out of order. */
export const $compassSaving = atom(false);

export async function loadCompass(): Promise<void> {
  try {
    const d = await getCompass();
    $compass.set({ hide: d.hide ?? [], marks: d.marks ?? [] });
    $compassLoaded.set(true);
  } catch {
    $compassLoaded.set(false);
  }
}

/** Optimistic save: show next at once, keep the server's answer, or restore the previous document. */
export async function saveCompass(next: CompassDoc): Promise<boolean> {
  if ($compassSaving.get()) return false;
  $compassSaving.set(true);
  const prev = $compass.get();
  $compass.set(next);
  try {
    $compass.set(await putCompass(next));
    return true;
  } catch (e) {
    $compass.set(prev);
    toast(`羅盤標記沒有存起來：${e instanceof ApiError ? describeError(e.code, e.message) : "無法連線到伺服器"}`, 5000);
    return false;
  } finally {
    $compassSaving.set(false);
  }
}

/** "標記 N" with the smallest N no mark uses yet. */
export function nextMarkName(doc: CompassDoc): string {
  const used = new Set(doc.marks.map((m) => m.name));
  for (let n = 1; ; n++) if (!used.has(`標記 ${n}`)) return `標記 ${n}`;
}

const ID_CHARS = "abcdefghijklmnopqrstuvwxyz0123456789";

/** 6 random lowercase letters or digits that no mark in doc uses. */
export function newMarkId(doc: CompassDoc, rand: () => number = Math.random): string {
  const used = new Set(doc.marks.map((m) => m.id));
  for (;;) {
    let id = "";
    for (let i = 0; i < 6; i++) id += ID_CHARS[Math.floor(rand() * ID_CHARS.length) % ID_CHARS.length];
    if (!used.has(id)) return id;
  }
}

/** The server's rule: 1–24 characters after trimming, counted as code points, without control or format characters. */
export function validMarkName(name: string): boolean {
  const t = name.trim(), n = Array.from(t).length;
  return n >= 1 && n <= MAX_NAME_CHARS && !/[\p{Cc}\p{Cf}]/u.test(t);
}

export function addMark(doc: CompassDoc, name: string, pos: [number, number]): CompassDoc {
  return { ...doc, marks: [...doc.marks, { id: newMarkId(doc), name: name.trim(), pos }] };
}

export function removeMark(doc: CompassDoc, id: string): CompassDoc {
  return { ...doc, marks: doc.marks.filter((m) => m.id !== id) };
}

export function setHidden(doc: CompassDoc, key: "estate" | "library", hidden: boolean): CompassDoc {
  const rest = doc.hide.filter((h) => h !== key);
  return { ...doc, hide: hidden ? [...rest, key] : rest };
}
